import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getReportById, executeReport, exportReport, downloadExport } from '../../../api/reports';
import './ReportViewer.css';

function ReportViewer() {
  const { reportId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [result, setResult] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [parameters, setParameters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(50);

  useEffect(() => {
    loadReport();
  }, [reportId]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const reportData = await getReportById(reportId);
      setReport(reportData);

      // If no result passed from previous page, execute the report
      if (!result) {
        await handleExecute();
      }
    } catch (err) {
      setError('Failed to load report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    setError(null);
    try {
      const executionResult = await executeReport(reportId, parameters);
      setResult(executionResult);
      setCurrentPage(1);
    } catch (err) {
      setError('Failed to execute report: ' + (err.response?.data?.message || err.message));
    } finally {
      setExecuting(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await exportReport(reportId, format, parameters);
      const extension = format === 'EXCEL' ? 'xlsx' : format.toLowerCase();
      downloadExport(response.data, `${report.reportName}.${extension}`);
    } catch (err) {
      alert('Failed to export report: ' + err.message);
    }
  };

  const renderTable = () => {
    if (!result || !result.success) return null;

    const columns = result.columns || [];
    const data = result.data || [];
    const totalRows = data.length;

    // Pagination
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
    const paginatedData = data.slice(startIndex, endIndex);
    const totalPages = Math.ceil(totalRows / rowsPerPage);

    return (
      <div className="report-results">
        <div className="results-header">
          <div className="results-info">
            <h3>Results</h3>
            <span className="record-count">{totalRows} record{totalRows !== 1 ? 's' : ''}</span>
          </div>
          <div className="results-actions">
            <button className="btn-secondary btn-sm" onClick={() => handleExport('PDF')}>Export PDF</button>
            <button className="btn-secondary btn-sm" onClick={() => handleExport('EXCEL')}>Export Excel</button>
            <button className="btn-secondary btn-sm" onClick={() => handleExport('CSV')}>Export CSV</button>
          </div>
        </div>

        <div className="table-container">
          <table className="report-table">
            <thead>
              <tr>
                {columns.map(column => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map(column => (
                    <td key={column}>
                      {row[column] !== null && row[column] !== undefined
                        ? String(row[column])
                        : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="btn-secondary btn-sm"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-secondary btn-sm"
            >
              Last
            </button>
          </div>
        )}

        {result.executionTime && (
          <div className="execution-info">
            <span>Executed in {result.executionTime}ms</span>
            {result.executedAt && (
              <span>at {new Date(result.executedAt).toLocaleString()}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSummary = () => {
    if (!result || !result.summary) return null;

    return (
      <div className="report-summary">
        <h3>Summary</h3>
        <div className="summary-grid">
          {Object.entries(result.summary).map(([key, value]) => (
            <div key={key} className="summary-item">
              <span className="summary-label">{key}:</span>
              <span className="summary-value">{value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="report-viewer-container">
        <div className="loading">Loading report...</div>
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className="report-viewer-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="report-viewer-container">
      <div className="report-viewer-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/admin/reports')}>
            ← Back to Reports
          </button>
          <div className="report-info">
            <h1>{report?.reportName}</h1>
            {report?.description && <p className="report-description">{report.description}</p>}
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate(`/admin/reports/builder/${reportId}`)}
          >
            Edit Report
          </button>
          <button
            className="btn-primary"
            onClick={handleExecute}
            disabled={executing}
          >
            {executing ? 'Running...' : 'Run Report'}
          </button>
        </div>
      </div>

      {report && (
        <div className="report-metadata">
          <div className="metadata-item">
            <span className="metadata-label">Data Source:</span>
            <span className="metadata-value">{report.dataSource}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Report Type:</span>
            <span className="metadata-value">{report.reportType}</span>
          </div>
          {report.category && (
            <div className="metadata-item">
              <span className="metadata-label">Category:</span>
              <span className="metadata-value">{report.category}</span>
            </div>
          )}
          <div className="metadata-item">
            <span className="metadata-label">Last Executed:</span>
            <span className="metadata-value">
              {report.lastExecuted
                ? new Date(report.lastExecuted).toLocaleString()
                : 'Never'}
            </span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Execution Count:</span>
            <span className="metadata-value">{report.executionCount || 0}</span>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {executing && (
        <div className="executing-overlay">
          <div className="spinner"></div>
          <p>Executing report...</p>
        </div>
      )}

      {result && result.success && (
        <>
          {renderSummary()}
          {renderTable()}
        </>
      )}

      {result && !result.success && (
        <div className="error-message">
          Report execution failed: {result.error || 'Unknown error'}
        </div>
      )}

      {!result && !executing && (
        <div className="no-results">
          <p>Click "Run Report" to execute and view results</p>
        </div>
      )}
    </div>
  );
}

export default ReportViewer;
